<!-- 
화면명 : Print.vue
작성일 : 2026-08-14
설명   : 자재발주서(A4) 출력 팝업 화면
-->
<template>
  <div class="p-3 bg-white text-slate-800 min-h-screen select-none font-sans text-xs sm:text-sm">
    <!-- 상단 헤더 영역 -->
    <div class="flex justify-between items-center pb-2">
      <div class="flex items-center space-x-2">
        <span class="inline-block w-3.5 h-3.5 rounded-full bg-blue-600 border-2 border-blue-200"></span>
        <span class="font-bold text-lg text-slate-900">{{ titleText }}</span>
        <span class="text-red-600 font-bold text-base tracking-wider ml-2">
          화영 ERP 화영 ERP 화영 ERP
        </span>
      </div>

      <!-- 상단 버튼 그룹 -->
      <div class="flex items-center space-x-1.5">
        <button v-if="gbn === 'M'"
          @click="btnPrintClick('WY05RSR_2QR_R')"
          class="btn-action"
        >
          <i class="fa fa-print mr-1 text-blue-600"></i> 건별
        </button>
        <button v-if="gbn === 'M'"
          @click="btnPrintClick('WY05RSR_3_B_R')" 
          class="btn-action"
        >
          <i class="fa fa-print mr-1 text-blue-600"></i> 5행
        </button>
        <button v-if="gbn === 'M'"
          @click="btnPrintClick('MT05LABEL')"
          class="btn-action"
        >
          <i class="fa fa-print mr-1 text-blue-600"></i> 라벨
        </button>


        <button v-if="gbn !== 'M'"
          @click="btnPrintClick('PR05RSR_2QR_R')"
          class="btn-action"
        >
          <i class="fa fa-print mr-1 text-blue-600"></i> 건별
        </button>
        <button v-if="gbn !== 'M'"
          @click="btnPrintClick('PR05LABEL')"
          class="btn-action"
        >
          <i class="fa fa-print mr-1 text-blue-600"></i> 라벨
        </button>
        <button
          @click="btnSaveHeatNo"
          class="btn-action"
        >
          <i class="fa fa-save mr-1 text-blue-600"></i> HEATNO 등록
        </button>
        <button
          @click="btnClose"
          class="btn-action text-red-600 border-red-200 hover:bg-red-50"
        >
          <i class="fa fa-times mr-1 text-red-500"></i> 닫기
        </button>
      </div>
    </div>

    <!-- 헤더 하단 구분선 -->
    <div class="h-0.5 bg-slate-800 w-full mb-2"></div>

    <!-- 발주번호 섹션 헤더 -->
    <div class="flex justify-between items-center px-1 mb-1">
      <div class="flex items-center space-x-1.5">
        <span class="inline-block w-2.5 h-2.5 rounded-full bg-orange-500 border border-orange-300"></span>
        <span class="font-bold text-sm text-slate-800">발주번호</span>
      </div>
      <button
        @click="btnClear"
        class="btn-clear"
      >
        <i class="fa fa-trash-alt mr-1 text-blue-600"></i> Clear
      </button>
    </div>

    <!-- 3단 발주번호 / HeatNo 테이블 목록 (총 30개) -->
    <div class="border border-gray-300 rounded-lg p-2 bg-white shadow-xs">
      <div class="grid grid-cols-3 gap-3">
        <div v-for="(col, colIdx) in 3" :key="colIdx" class="w-full">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-slate-100 border-b border-gray-300 text-slate-700">
                <th class="py-1 px-1 text-center font-bold w-10 border-r border-gray-200">No</th>
                <th class="py-1 px-2 text-center font-bold border-r border-gray-200">발주번호</th>
                <th class="py-1 px-2 text-center font-bold">HeatNo</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="rowIdx in 10"
                :key="rowIdx"
                class="border-b border-dotted border-gray-300 hover:bg-blue-50/40 transition-colors"
              >
                <td class="text-center font-bold text-slate-700 py-0.5 text-xs">
                  {{ (colIdx * 10) + rowIdx }}
                </td>
                <td class="p-0.5">
                  <input
                    type="text"
                    v-model="orderList[(colIdx * 10) + rowIdx - 1].pos"
                    @input="onPosInput((colIdx * 10) + rowIdx - 1)"
                    @paste="onPosPaste($event, (colIdx * 10) + rowIdx - 1)"
                    class="w-full border border-gray-300 rounded px-1.5 py-0.5 text-center text-xs font-mono font-medium focus:border-blue-500 focus:bg-blue-50/20 focus:outline-none"
                  />
                </td>
                <td class="p-0.5">
                  <input
                    type="text"
                    v-model="orderList[(colIdx * 10) + rowIdx - 1].heatNo"
                    class="w-full border border-gray-300 rounded px-1.5 py-0.5 text-center text-xs font-mono font-medium focus:border-blue-500 focus:bg-blue-50/20 focus:outline-none"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 하단 라벨위치 및 거래선 바 -->
    <div class="flex items-center gap-6 mt-2.5 p-2 bg-slate-50 border border-gray-200 rounded text-xs sm:text-sm font-semibold">
      <div class="flex items-center space-x-2 pl-2">
        <span class="text-slate-800">라벨위치</span>
        <input
          type="number"
          v-model="labelPosition"
          min="1"
          class="w-14 border border-gray-300 rounded px-2 py-0.5 text-center bg-white font-mono focus:border-blue-500 focus:outline-none"
        />
      </div>

      <div class="flex items-center space-x-2 pl-4">
        <span class="text-slate-800">거래선</span>
        <input
          type="text"
          :value="vendorCode"
          readonly
          class="w-48 border border-gray-300 rounded px-2 py-0.5 text-center bg-gray-100 font-mono text-slate-800 focus:outline-none cursor-not-allowed"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useUserStore } from "@/store/userStore";
import { notify } from "@kyvg/vue3-notification";
import { apiExecuteReport } from "@/api/SCM/common";

const route = useRoute();
const userStore = useUserStore();

// 처리구분 (M: 자재, P: 외주)
const gbn = computed(() => {
  const g = (route.query.gbn || "M").toString().toUpperCase();
  return g === "P" ? "P" : "M";
});

// 타이틀: M이면 자재발주서 출력(A4), P이면 외주발주서 출력(A4)
const titleText = computed(() =>
  gbn.value === "P" ? "외주발주서 출력" : "자재발주서 출력"
);

// 30개의 발주번호 및 HeatNo 데이터 배열 초기화
const orderList = ref(
  Array.from({ length: 30 }, (_, index) => ({
    no: index + 1,
    pos: "",
    heatNo: "",
  }))
);

const labelPosition = ref(1);
const vendorCode = computed(() => userStore.userVendCd.substr(0, 3) +'-' + userStore.userVendCd.substr(3, 2) +'-' + userStore.userVendCd.substr(5, 5));

// 데이터 반영 공통 함수
function applyOrderData(items) {
  // 기존 30개 행 초기화
  orderList.value.forEach((item) => {
    item.pos = "";
    item.heatNo = "";
  });

  if (Array.isArray(items)) {
    items.slice(0, 30).forEach((item, idx) => {
      if (item && orderList.value[idx]) {
        orderList.value[idx].pos = item.pos || item.POS || "";
        orderList.value[idx].heatNo = item.heatNo || item.M05HEATNO || "";
      }
    });
  }
}

// 1. localStorage에서 데이터 읽기
function loadPassedData() {
  try {
    const rawData = localStorage.getItem("print_order_items");
    if (rawData) {
      const parsed = JSON.parse(rawData);
      const items = Array.isArray(parsed) ? parsed : parsed.items;
      applyOrderData(items);
    }
  } catch (err) {
    console.error("데이터 로드 실패:", err);
  }
}

// 2. 부모창으로부터의 postMessage 수신
function handlePostMessage(event) {
  if (event.data && event.data.type === "UPDATE_PRINT_DATA") {
    applyOrderData(event.data.items);
  }
}

// 3. 브라우저 간 localStorage 변경 이벤트 수신
function handleStorageChange(event) {
  if (event.key === "print_order_items" && event.newValue) {
    try {
      const parsed = JSON.parse(event.newValue);
      const items = Array.isArray(parsed) ? parsed : parsed.items;
      applyOrderData(items);
    } catch (err) {
      console.error("storage 데이터 파싱 실패:", err);
    }
  }
}

// 4. URL 라우트 쿼리 변경 감지
watch(
  () => route.query.t,
  () => {
    loadPassedData();
  }
);

watch(titleText, (newTitle) => {
  document.title = newTitle;
});

// 발주번호 1번 입력값 분할 및 1~30번 재배열 처리
function handleFirstPosChange() {
  const val = orderList.value[0]?.pos;
  if (!val || typeof val !== "string") return;

  // 개행문자(\r\n, \n, \r) 및 탭(\t)을 쉼표(,)로 치환
  const normalized = val.replace(/[\r\n\t]+/g, ",");

  // 쉼표(,)가 포함된 경우
  if (normalized.includes(",")) {
    // 쉼표로 split 후 앞뒤 공백 제거 및 빈 문자열 제외
    const tokens = normalized
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item !== "");

    // 분할된 항목이 2개 이상인 경우 1~30번까지 재배열
    if (tokens.length >= 2) {
      for (let i = 0; i < 30; i++) {
        if (i < tokens.length) {
          orderList.value[i].pos = tokens[i];
        } else {
          orderList.value[i].pos = "";
        }
      }

      notify({
        title: "자동 분할",
        text: `${Math.min(tokens.length, 30)}개의 발주번호가 1~30번에 자동 재배열되었습니다.`,
        type: "info",
      });
    }
  }
}

// 발주번호 입력 이벤트 핸들러
function onPosInput(index) {
  if (index === 0) {
    handleFirstPosChange();
  }
}

// 발주번호 붙여넣기(Paste) 이벤트 핸들러
function onPosPaste(event, index) {
  if (index === 0) {
    const pastedText = event.clipboardData?.getData("text") || "";
    if (
      pastedText.includes("\n") ||
      pastedText.includes("\r") ||
      pastedText.includes(",")
    ) {
      event.preventDefault();
      orderList.value[0].pos = pastedText;
      handleFirstPosChange();
    }
  }
}

// 1번 항목 감시
watch(
  () => orderList.value[0]?.pos,
  (newVal) => {
    if (
      newVal &&
      (newVal.includes(",") || newVal.includes("\n") || newVal.includes("\r"))
    ) {
      handleFirstPosChange();
    }
  }
);

onMounted(() => {
  document.title = titleText.value;
  loadPassedData();

  window.addEventListener("message", handlePostMessage);
  window.addEventListener("storage", handleStorageChange);
});

onUnmounted(() => {
  window.removeEventListener("message", handlePostMessage);
  window.removeEventListener("storage", handleStorageChange);
});

// Clear 버튼: 모든 입력 필드 초기화
function btnClear() {
  orderList.value.forEach((item) => {
    item.pos = "";
    item.heatNo = "";
  });
  notify({
    title: "알림",
    text: "발주번호 입력란이 초기화되었습니다.",
    type: "info",
  });
}

// 닫기 버튼: 팝업창 닫기
function btnClose() {
  window.close();
}

// HEATNO 등록 버튼
function btnSaveHeatNo() {
  const activeItems = orderList.value.filter((item) => item.pos.trim() !== "");
  if (activeItems.length === 0) {
    notify({
      title: "알림",
      text: "등록할 발주번호가 없습니다.",
      type: "warn",
    });
    return;
  }

  notify({
    title: "성공",
    text: `${activeItems.length}건의 HeatNo가 등록되었습니다.`,
    type: "success",
  });
}

// 출력 버튼 (건별, 5행, 라벨)
async function btnPrintClick(type) {
  const activeKeys = orderList.value
    .map((item) => item.pos.trim())
    .filter((pos) => pos !== "")
    .join(",");

  if (!activeKeys) {
    notify({
      title: "알림",
      text: "출력할 발주번호를 1개 이상 입력해주세요.",
      type: "warn",
    });
    return;
  }

  const typeLabels = {
    WY05RSR_2QR_R: "건별",
    PR05RSR_2QR_R: "건별",
    WY05RSR_3_B_R: "5행",
    MT05LABEL: "라벨",
    PR05LABEL: "라벨",
  };  

  const baseTitle = gbn.value === "P" ? "외주발주서" : "자재발주서";

  const searchParams = {
    
    ID: "W000\\W000\\R-OCM\\" + type,
    Parameter: {
      "@GBN": gbn.value,
      "@CUSNO": userStore.userVendCd,
      "@POSKEY": activeKeys,
      "@PRINT_TYPE": type,
      "@LABEL_POS": labelPosition.value,
    },
  };

  const exportParams = {
    title: `${baseTitle}(${typeLabels[type]})`,
  };

  try {
    await apiExecuteReport(searchParams, exportParams);
  } catch (err) {
    console.error("출력 실패:", err);
  }
}
</script>

<style scoped>
.btn-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #1e3a8a;
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.btn-action:hover {
  background-color: #dbeafe;
  border-color: #93c5fd;
}

.btn-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #1e3a8a;
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.btn-clear:hover {
  background-color: #e0f2fe;
  border-color: #7dd3fc;
}
</style>
